import { useCurrentUser } from '@qualio/ui-components';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentFormat } from '../../../../api/model/document';
import { ChangeControl } from '../../../../types/DocumentCreate';
import { isOwner } from '../../../../util/CurrentUser';
import { EditorMode } from '../../../../util/EditModeUtils';
import { GenerateCCComponent } from '../../AiPoc/GenerateCC/GenerateCC';
import { InlineAttachmentPreviewModal } from '../../InlineAttachmentPreview/InlineAttachmentPreviewModal';
import { ToolbarWrapper } from '../../ToolbarWrapper/ToolbarWrapper';
import { DocumentOverviewContext } from '../Context';
import styles from '../DocumentOverview.module.css';
import { HTMLViewer } from '../HTMLViewer/index';
import { QChangeControlMultiRootEditor } from '../QChangeControlMultiRootEditor/QChangeControlMultiRootEditor';
import { ChangeControlEmptyState } from './ChangeControlEmptyState';
import { ChangeControlSectionsManager } from './ChangeControlSectionsManager';
import { ChangeControlSidebarWrapper } from './ChangeControlSidebarWrapper';

type ChangeControlProps = {
  changeControl: ChangeControl | null | undefined;
  refetchChangeControl: () => void;
};

export const ChangeControlDisplay = ({
  changeControl,
  refetchChangeControl,
}: ChangeControlProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { qualioDocument, currentEditorMode, commentsPermission } = useContext(
    DocumentOverviewContext,
  );
  const isReadOnly = useMemo(() => {
    const hasEditPermission = commentsPermission > 0;
    return (
      !hasEditPermission ||
      qualioDocument.status_id === 'deleted' ||
      qualioDocument.status_id === 'effective' ||
      !qualioDocument.is_most_recent_version
    );
  }, [
    commentsPermission,
    qualioDocument.is_most_recent_version,
    qualioDocument.status_id,
  ]);

  const [editors, setEditors] = useState([]);
  const aiEnabled = currentUser.company.ai_enabled;

  const [attachmentForPreview, setAttachmentForPreview] = useState<string>();

  const showAttachmentForPreviewCallback = useCallback(
    (attachmentId: string) => {
      setAttachmentForPreview(attachmentId);
    },
    [],
  );

  const widthRef = useRef<HTMLDivElement>(null);

  if (!changeControl) {
    if (isOwner(currentUser, qualioDocument)) {
      return <ChangeControlEmptyState onCreated={refetchChangeControl} />;
    }
    return <div>{t('messages.changeControlHasNotBeenCreatedYet')}</div>;
  }

  const shouldUseMultiRootEditor =
    qualioDocument.editor_version?.includes('multiroot') && !isReadOnly;

  if (shouldUseMultiRootEditor) {
    return (
      <>
        {aiEnabled &&
          qualioDocument.document_format ===
            DocumentFormat.QualioNativeDocument && (
            <GenerateCCComponent
              changeControl={changeControl}
              editors={editors}
            />
          )}
        {attachmentForPreview && (
          <InlineAttachmentPreviewModal
            currentUser={currentUser}
            attachmentId={attachmentForPreview}
            onClose={() => setAttachmentForPreview(undefined)}
          />
        )}
        <QChangeControlMultiRootEditor
          handleAttachmentClick={showAttachmentForPreviewCallback}
          handleSessionDisconnectionEvent={() =>
            console.log('handleSessionDisconnectionEvent')
          }
          setEditors={setEditors}
        />
      </>
    );
  }

  return (
    <>
      {aiEnabled &&
        currentEditorMode !== EditorMode.VIEW &&
        qualioDocument.document_format ===
          DocumentFormat.QualioNativeDocument && (
          <GenerateCCComponent
            changeControl={changeControl}
            editors={editors}
          />
        )}

      <ToolbarWrapper
        className={`${styles['document-overview__toolbar']}`}
        id="changeControlToolbarWrapper"
      >
        {/*Toolbar will be placed during editing*/}
      </ToolbarWrapper>
      {attachmentForPreview && (
        <InlineAttachmentPreviewModal
          currentUser={currentUser}
          attachmentId={attachmentForPreview}
          onClose={() => setAttachmentForPreview(undefined)}
        />
      )}
      <ChangeControlSidebarWrapper
        ref={widthRef}
        changeControl={changeControl}
        handleAttachmentClick={showAttachmentForPreviewCallback}
      >
        {isReadOnly ? (
          <HTMLViewer sections={changeControl.sections} />
        ) : (
          <ChangeControlSectionsManager
            changeControl={changeControl}
            setEditors={setEditors}
            editors={editors}
            handleAttachmentClick={showAttachmentForPreviewCallback}
          />
        )}
      </ChangeControlSidebarWrapper>
    </>
  );
};
